import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Switch,
  Table,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import AppContext from "../../../context/AppContext";
import { SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import {
  EARLY_PAYOUT_ACTION_LABELS,
  EARLY_PAYOUT_ADVANTAGE_INPUT_PROPS,
  EARLY_PAYOUT_ADVANTAGE_RULES,
  EARLY_PAYOUT_CONFIRM_MESSAGES,
  EARLY_PAYOUT_ERROR_MESSAGES,
  EARLY_PAYOUT_FORM_LABELS,
  EARLY_PAYOUT_MODAL_TITLES,
  EARLY_PAYOUT_PLACEHOLDERS,
  EARLY_PAYOUT_RULE_TYPE_OPTIONS,
  EARLY_PAYOUT_SELECTORS_DOWNLOAD_FILENAME,
  EARLY_PAYOUT_TABLE_LABELS,
  EARLY_PAYOUT_TABS,
  EARLY_PAYOUT_TEST_IDS,
  SELECTOR_OPTIONS,
  SOCCER_TOTAL_DEFAULT_CONDITIONS,
} from "./earlyPayoutConstants";
import {
  EARLY_PAYOUT_REPORTING_DOWNLOAD_FILENAME,
  EARLY_PAYOUT_REPORTING_TAB,
} from "./earlyPayoutReportingConstants";
import { buildEarlyPayoutReportingCsv } from "./earlyPayoutReportingUtils";
import { EARLY_PAYOUT_REPORTING_COLUMNS } from "./earlyPayoutReportingColumns";
import {
  mapEventsDropdownToSelectOptions,
  mapLeaguesDropdownToSelectOptions,
  mapMarketsDropdownToSelectOptions,
  mapRulesetsToSelectOptions,
  mapSportsDropdownToSelectOptions,
  useCreateEarlyPayoutRule,
  useCreateEarlyPayoutSelector,
  useDeleteEarlyPayoutRule,
  useDeleteEarlyPayoutSelector,
  useEarlyPayoutEventsDropdown,
  useEarlyPayoutLeaguesDropdown,
  useEarlyPayoutMarketsDropdown,
  useEarlyPayoutRules,
  useEarlyPayoutSelectors,
  useEarlyPayoutSportsDropdown,
  useUpdateEarlyPayoutRule,
  useUpdateEarlyPayoutSelector,
} from "../../../queries/sportsbook/earlyPayout";
import type {
  CreateRulesetFormValues,
  CreateSelectorFormValues,
  EarlyPayoutActiveTab,
  EarlyPayoutReportingRow,
  EarlyPayoutRuleset,
  EarlyPayoutSelector,
} from "./earlyPayoutTypes";
import {
  buildEarlyPayoutRulesetsCsv,
  buildEarlyPayoutSelectorsCsv,
  getEarlyPayoutRulesetMarketLabel,
  mapSoccerTotalConditionsToRuleLines,
  normalizeEarlyPayoutAdvantage,
  normalizeEarlyPayoutRuleType,
  triggerClientCsvFileDownload,
} from "./earlyPayoutUtils";
// TODO(SPENG-2296): replace MOCK_REPORTING_ROWS with the reporting API hook once available.
import { MOCK_REPORTING_ROWS } from "./earlyPayoutMockData";
import SoccerTotalConditions from "./SoccerTotalConditions";
import SoccerTotalConditionsView from "./SoccerTotalConditionsView";
import { useIsDevMode } from "../../../hooks/useIsDevMode";
import styles from "./EarlyPayout.module.css";

type RulesetModalMode = "create" | "edit";

type PendingDeleteTarget =
  | { type: "ruleset"; record: EarlyPayoutRuleset }
  | { type: "selector"; record: EarlyPayoutSelector };

const showEarlyPayoutApiSuccessMessage = (apiMessage: string) => {
  if (apiMessage) {
    message.success(apiMessage);
  }
};

const showEarlyPayoutApiErrorMessage = (error: unknown) => {
  const apiMessage = getApiErrorMessage(error);
  if (apiMessage) {
    message.error(apiMessage);
  }
};

/**
 * Sportsbook -> Configuration: Early Payout rulesets and selectors (admin API).
 */
const EarlyPayout: React.FC = () => {
  usePageTitle(AppStrings.EARLY_PAYOUT);
  const { setActiveSidebarTab } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<EarlyPayoutActiveTab>("rulesets");
  const {
    data: rulesets = [],
    isLoading: isRulesetsLoading,
    isError: isRulesetsError,
  } = useEarlyPayoutRules();
  const { mutate: updateEarlyPayoutRule, isPending: isUpdatingRuleset } =
    useUpdateEarlyPayoutRule();
  const { mutate: deleteEarlyPayoutRule, isPending: isDeletingRuleset } =
    useDeleteEarlyPayoutRule();
  const { mutate: deleteEarlyPayoutSelector, isPending: isDeletingSelector } =
    useDeleteEarlyPayoutSelector();
  const { mutate: createEarlyPayoutRule, isPending: isCreatingRuleset } =
    useCreateEarlyPayoutRule();
  const { mutate: createEarlyPayoutSelector, isPending: isCreatingSelector } =
    useCreateEarlyPayoutSelector();
  const [isCreateRulesetOpen, setIsCreateRulesetOpen] = useState(false);
  const [isCreateSelectorOpen, setIsCreateSelectorOpen] = useState(false);
  const [rulesetModalMode, setRulesetModalMode] =
    useState<RulesetModalMode>("create");
  const isCreateRulesetModalOpen =
    isCreateRulesetOpen && rulesetModalMode === "create";
  const {
    data: sportsDropdown = [],
    isLoading: isSportsDropdownLoading,
    isError: isSportsDropdownError,
  } = useEarlyPayoutSportsDropdown(isCreateRulesetModalOpen || isCreateSelectorOpen);
  const {
    data: marketsDropdown = [],
    isLoading: isMarketsDropdownLoading,
    isError: isMarketsDropdownError,
  } = useEarlyPayoutMarketsDropdown(isCreateRulesetModalOpen);
  const [selectedRuleset, setSelectedRuleset] =
    useState<EarlyPayoutRuleset | null>(null);
  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<PendingDeleteTarget | null>(null);
  const [rulesetForm] = Form.useForm<CreateRulesetFormValues>();
  const [selectorForm] = Form.useForm<CreateSelectorFormValues>();
  const selectedRulesetRuleType = Form.useWatch("ruleType", rulesetForm);
  const selectedSelectorType = Form.useWatch("selector", selectorForm);
  const selectedSportId = Form.useWatch("sportId", selectorForm);
  const selectedTournamentId = Form.useWatch("tournamentId", selectorForm);
  const isReportingEnabled = useIsDevMode();
  const isDevMode = useIsDevMode();
  const tabs = useMemo(
    () =>
      isReportingEnabled
        ? [...EARLY_PAYOUT_TABS, EARLY_PAYOUT_REPORTING_TAB]
        : EARLY_PAYOUT_TABS,
    [isReportingEnabled],
  );
  const isRulesetsTabActive = activeTab === "rulesets";
  const isSelectorsTabActive = activeTab === "selectors";
  const isReportingTabActive = activeTab === "reporting";
  const isEditingRuleset = rulesetModalMode === "edit";
  const isCreateTotalRuleType =
    !isEditingRuleset && selectedRulesetRuleType === "TOTAL";
  const isEditTotalRuleType =
    isEditingRuleset &&
    normalizeEarlyPayoutRuleType(selectedRuleset?.ruleType) === "TOTAL";
  const {
    data: selectors = [],
    isLoading: isSelectorsLoading,
    isError: isSelectorsError,
  } = useEarlyPayoutSelectors(isSelectorsTabActive);
  const { mutate: updateEarlyPayoutSelector, isPending: isUpdatingSelector } =
    useUpdateEarlyPayoutSelector();
  const [selectorRuleViewTarget, setSelectorRuleViewTarget] =
    useState<EarlyPayoutSelector | null>(null);
  const selectorRuleViewRuleType = normalizeEarlyPayoutRuleType(
    selectorRuleViewTarget?.rule?.ruleType,
  );
  const isSelectorRuleViewTotal = selectorRuleViewRuleType === "TOTAL";
  const [togglingSelectorId, setTogglingSelectorId] = useState<string | null>(
    null,
  );
  const sportSelectOptions = useMemo(
    () => mapSportsDropdownToSelectOptions(sportsDropdown),
    [sportsDropdown],
  );
  const marketSelectOptions = useMemo(
    () => mapMarketsDropdownToSelectOptions(marketsDropdown),
    [marketsDropdown],
  );
  const selectorSportRuleOptions = useMemo(() => {
    if (!selectedSportId) {
      return [];
    }
    return mapRulesetsToSelectOptions(
      rulesets.filter((ruleset) => ruleset.sportId === selectedSportId),
    );
  }, [rulesets, selectedSportId]);
  const isSportSelectorType = selectedSelectorType === "sport";
  const isTournamentSelectorType = selectedSelectorType === "tournament";
  const isEventSelectorType = selectedSelectorType === "event";
  const needsLeagueDropdown = isTournamentSelectorType || isEventSelectorType;
  const canCreateSelectorType =
    isSportSelectorType || isTournamentSelectorType || isEventSelectorType;
  const {
    data: leaguesDropdown = [],
    isLoading: isLeaguesDropdownLoading,
    isError: isLeaguesDropdownError,
  } = useEarlyPayoutLeaguesDropdown(
    selectedSportId,
    isCreateSelectorOpen && needsLeagueDropdown,
  );
  const {
    data: eventsDropdown = [],
    isLoading: isEventsDropdownLoading,
    isError: isEventsDropdownError,
  } = useEarlyPayoutEventsDropdown(
    selectedTournamentId,
    isCreateSelectorOpen && isEventSelectorType,
  );
  const leagueSelectOptions = useMemo(
    () => mapLeaguesDropdownToSelectOptions(leaguesDropdown),
    [leaguesDropdown],
  );
  const eventSelectOptions = useMemo(
    () => mapEventsDropdownToSelectOptions(eventsDropdown),
    [eventsDropdown],
  );
  const deleteModalTitle =
    pendingDeleteTarget?.type === "ruleset"
      ? EARLY_PAYOUT_CONFIRM_MESSAGES.deleteRulesetTitle
      : EARLY_PAYOUT_CONFIRM_MESSAGES.deleteSelectorTitle;

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.SPORTSBOOK, "EARLY_PAYOUT");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    if (isRulesetsError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadRulesets);
    }
  }, [isRulesetsError]);

  useEffect(() => {
    if (isSportsDropdownError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadSportsDropdown);
    }
  }, [isSportsDropdownError]);

  useEffect(() => {
    if (isMarketsDropdownError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadMarketsDropdown);
    }
  }, [isMarketsDropdownError]);

  useEffect(() => {
    if (isSelectorsError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadSelectors);
    }
  }, [isSelectorsError]);

  useEffect(() => {
    if (isLeaguesDropdownError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadLeaguesDropdown);
    }
  }, [isLeaguesDropdownError]);

  useEffect(() => {
    if (isEventsDropdownError) {
      message.error(EARLY_PAYOUT_ERROR_MESSAGES.loadEventsDropdown);
    }
  }, [isEventsDropdownError]);

  const handleExport = () => {
    let exportErrorMessage: string = EARLY_PAYOUT_ERROR_MESSAGES.exportRulesets;
    try {
      if (isSelectorsTabActive) {
        exportErrorMessage = EARLY_PAYOUT_ERROR_MESSAGES.exportSelectors;
        triggerClientCsvFileDownload(
          buildEarlyPayoutSelectorsCsv(selectors),
          EARLY_PAYOUT_SELECTORS_DOWNLOAD_FILENAME,
        );
        return;
      }
      if (isReportingTabActive) {
        exportErrorMessage = EARLY_PAYOUT_ERROR_MESSAGES.exportReporting;
        triggerClientCsvFileDownload(
          buildEarlyPayoutReportingCsv(MOCK_REPORTING_ROWS),
          EARLY_PAYOUT_REPORTING_DOWNLOAD_FILENAME,
        );
        return;
      }
      triggerClientCsvFileDownload(buildEarlyPayoutRulesetsCsv(rulesets));
    } catch (error) {
      // eslint-disable-next-line no-console -- mirrors Reporting Summary export diagnostics.
      console.error("[EarlyPayoutExport]", error);
      message.error(exportErrorMessage);
    }
  };

  const handleCloseCreateRuleset = () => {
    if (isCreateRulesetOpen) {
      rulesetForm.resetFields();
    }
    setRulesetModalMode("create");
    setSelectedRuleset(null);
    setIsCreateRulesetOpen(false);
  };

  const handleCloseCreateSelector = () => {
    if (isCreateSelectorOpen) {
      selectorForm.resetFields();
    }
    setIsCreateSelectorOpen(false);
  };

  const handleCloseDeleteModal = () => {
    setPendingDeleteTarget(null);
  };

  const handleCloseSelectorRuleModal = () => {
    setSelectorRuleViewTarget(null);
  };

  const handleViewSelectorRule = useCallback((selector: EarlyPayoutSelector) => {
    setSelectorRuleViewTarget(selector);
  }, []);

  const handleCreateAction = () => {
    if (isRulesetsTabActive) {
      setRulesetModalMode("create");
      setSelectedRuleset(null);
      rulesetForm.resetFields();
      rulesetForm.setFieldsValue({ ruleType: "WINNER" });
      setIsCreateRulesetOpen(true);
      return;
    }
    selectorForm.resetFields();
    setIsCreateSelectorOpen(true);
  };

  const handleCreateRulesetSubmit = (values: CreateRulesetFormValues) => {
    if (isEditingRuleset && selectedRuleset) {
      const ruleName = values.ruleName ?? "";
      const ruleType = normalizeEarlyPayoutRuleType(selectedRuleset.ruleType);
      const advantage =
        ruleType === "TOTAL"
          ? 0
          : normalizeEarlyPayoutAdvantage(
              values.advantage,
              selectedRuleset.advantage,
            );

      updateEarlyPayoutRule(
        {
          ruleId: selectedRuleset.id,
          rule_name: ruleName,
          advantage,
          ...(ruleType === "TOTAL"
            ? {
                lines: mapSoccerTotalConditionsToRuleLines(
                  values.soccerTotalConditions,
                ),
              }
            : {}),
        },
        {
          onSuccess: (apiMessage) => {
            showEarlyPayoutApiSuccessMessage(apiMessage);
            handleCloseCreateRuleset();
          },
          onError: showEarlyPayoutApiErrorMessage,
        },
      );
      return;
    }

    const selectedSport = sportsDropdown.find(
      (sport) => sport.sport_id === values.sportId,
    );
    const selectedMarket = marketsDropdown.find(
      (market) => market.market_id === values.marketId,
    );

    if (!selectedSport || !selectedMarket) {
      return;
    }

    const ruleType = normalizeEarlyPayoutRuleType(values.ruleType);

    createEarlyPayoutRule(
      {
        rule_name: values.ruleName ?? "",
        rule_type: ruleType,
        sport_id: selectedSport.sport_id,
        sport_name: selectedSport.title,
        market_id: selectedMarket.market_id,
        market_name: selectedMarket.title,
        advantage:
          ruleType === "TOTAL"
            ? 0
            : normalizeEarlyPayoutAdvantage(values.advantage),
        ...(ruleType === "TOTAL"
          ? {
              lines: mapSoccerTotalConditionsToRuleLines(
                values.soccerTotalConditions,
              ),
            }
          : {}),
      },
      {
        onSuccess: (apiMessage) => {
          showEarlyPayoutApiSuccessMessage(apiMessage);
          handleCloseCreateRuleset();
        },
        onError: showEarlyPayoutApiErrorMessage,
      },
    );
  };

  const handleCreateSelectorSubmit = (values: CreateSelectorFormValues) => {
    const selectedSport = sportsDropdown.find(
      (sport) => sport.sport_id === values.sportId,
    );
    const selectedRule = rulesets.find((ruleset) => ruleset.id === values.ruleId);

    if (!selectedSport || !selectedRule) {
      return;
    }

    const mutationOptions = {
      onSuccess: (apiMessage: string) => {
        showEarlyPayoutApiSuccessMessage(apiMessage);
        handleCloseCreateSelector();
      },
      onError: showEarlyPayoutApiErrorMessage,
    };

    if (values.selector === "sport") {
      createEarlyPayoutSelector(
        {
          rule_id: selectedRule.id,
          selector_type: "sport",
          sport_id: selectedSport.sport_id,
          sport_name: selectedSport.title,
        },
        mutationOptions,
      );
      return;
    }

    const selectedLeague = leaguesDropdown.find(
      (league) => league.tournament_id === values.tournamentId,
    );

    if (!selectedLeague) {
      return;
    }

    if (values.selector === "tournament") {
      createEarlyPayoutSelector(
        {
          rule_id: selectedRule.id,
          selector_type: "tournament",
          sport_id: selectedSport.sport_id,
          sport_name: selectedSport.title,
          tournament_id: selectedLeague.tournament_id,
          tournament_name: selectedLeague.tournament_name,
          category_id: selectedLeague.category_id ?? null,
          category_name: selectedLeague.category_name ?? null,
        },
        mutationOptions,
      );
      return;
    }

    if (values.selector !== "event") {
      return;
    }

    const selectedEvent = eventsDropdown.find(
      (event) => event.event_id === values.eventId,
    );

    if (!selectedEvent) {
      return;
    }

    createEarlyPayoutSelector(
      {
        rule_id: selectedRule.id,
        selector_type: "event",
        sport_id: selectedSport.sport_id,
        sport_name: selectedSport.title,
        tournament_id: selectedLeague.tournament_id,
        tournament_name: selectedLeague.tournament_name,
        category_id: selectedLeague.category_id ?? null,
        category_name: selectedLeague.category_name ?? null,
        event_id: selectedEvent.event_id,
        event_name: selectedEvent.event_name,
      },
      mutationOptions,
    );
  };

  const handleSelectorTypeChange = () => {
    selectorForm.setFieldsValue({
      sportId: undefined,
      ruleId: undefined,
      tournamentId: undefined,
      eventId: undefined,
    });
  };

  const handleSelectorSportChange = () => {
    selectorForm.setFieldsValue({
      ruleId: undefined,
      tournamentId: undefined,
      eventId: undefined,
    });
  };

  const handleSelectorTournamentChange = () => {
    selectorForm.setFieldsValue({ eventId: undefined });
  };

  const handleTabChange = (nextTab: EarlyPayoutActiveTab) => {
    setActiveTab(nextTab);
    handleCloseDeleteModal();
    handleCloseCreateRuleset();
    handleCloseCreateSelector();
  };

  const handleEditRuleset = useCallback(
    (ruleset: EarlyPayoutRuleset) => {
      setRulesetModalMode("edit");
      setSelectedRuleset(ruleset);
      rulesetForm.setFieldsValue({
        ruleName: ruleset.ruleName,
        advantage: ruleset.advantage,
        soccerTotalConditions:
          ruleset.lines && ruleset.lines.length > 0
            ? ruleset.lines
            : [...SOCCER_TOTAL_DEFAULT_CONDITIONS],
      });
      setIsCreateRulesetOpen(true);
    },
    [rulesetForm],
  );

  const handleDeleteRuleset = useCallback((ruleset: EarlyPayoutRuleset) => {
    setPendingDeleteTarget({ type: "ruleset", record: ruleset });
  }, []);

  const handleDeleteSelector = useCallback((selector: EarlyPayoutSelector) => {
    setPendingDeleteTarget({ type: "selector", record: selector });
  }, []);

  const handleConfirmDelete = () => {
    if (!pendingDeleteTarget) return;

    if (pendingDeleteTarget.type === "ruleset") {
      const rulesetId = pendingDeleteTarget.record.id;

      deleteEarlyPayoutRule(rulesetId, {
        onSuccess: (apiMessage) => {
          showEarlyPayoutApiSuccessMessage(apiMessage);
          handleCloseDeleteModal();
        },
        onError: showEarlyPayoutApiErrorMessage,
      });
      return;
    }

    const selectorId = pendingDeleteTarget.record.selectorId;

    deleteEarlyPayoutSelector(selectorId, {
      onSuccess: (apiMessage) => {
        showEarlyPayoutApiSuccessMessage(apiMessage);
        handleCloseDeleteModal();
      },
      onError: showEarlyPayoutApiErrorMessage,
    });
  };

  const handleToggleSelectorStatus = useCallback(
    (selector: EarlyPayoutSelector, isActive: boolean) => {
      setTogglingSelectorId(selector.selectorId);
      updateEarlyPayoutSelector(
        { selectorId: selector.selectorId, is_active: isActive },
        {
          onSuccess: (apiMessage) => {
            showEarlyPayoutApiSuccessMessage(apiMessage);
          },
          onError: showEarlyPayoutApiErrorMessage,
          onSettled: () => {
            setTogglingSelectorId(null);
          },
        },
      );
    },
    [updateEarlyPayoutSelector],
  );

  const rulesetColumns = useMemo<ColumnsType<EarlyPayoutRuleset>>(
    () => [
      {
        title: EARLY_PAYOUT_TABLE_LABELS.ruleName,
        dataIndex: "ruleName",
        key: "ruleName",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.ruleType,
        dataIndex: "ruleType",
        key: "ruleType",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.sport,
        dataIndex: "sport",
        key: "sport",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.market,
        key: "market",
        render: (_, ruleset) => getEarlyPayoutRulesetMarketLabel(ruleset),
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.advantage,
        dataIndex: "advantage",
        key: "advantage",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.actions,
        key: "actions",
        render: (_, ruleset) => (
          <div className={styles["early-payout-row-actions"]}>
            <Button
              type="text"
              className={styles["early-payout-row-action-button"]}
              icon={<EditOutlined />}
              aria-label={`Edit ${ruleset.ruleName}`}
              onClick={() => handleEditRuleset(ruleset)}
            />
            <Button
              type="text"
              className={`${styles["early-payout-row-action-button"]} ${styles["early-payout-delete-action-button"]}`}
              icon={<DeleteOutlined />}
              aria-label={`Delete ${ruleset.ruleName}`}
              onClick={() => handleDeleteRuleset(ruleset)}
            />
          </div>
        ),
      },
    ],
    [handleDeleteRuleset, handleEditRuleset],
  );

  const selectorColumns = useMemo<ColumnsType<EarlyPayoutSelector>>(
    () => [
      {
        title: EARLY_PAYOUT_TABLE_LABELS.selector,
        dataIndex: "selector",
        key: "selector",
        width: 120,
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.id,
        dataIndex: "details",
        key: "details",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.status,
        dataIndex: "status",
        key: "status",
        width: 110,
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.ruleType,
        key: "ruleType",
        width: 110,
        render: (_, selector) =>
          selector.rule
            ? normalizeEarlyPayoutRuleType(selector.rule.ruleType)
            : "",
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.rule,
        key: "rule",
        width: 72,
        align: "center",
        render: (_, selector) => (
          <Button
            type="text"
            className={styles["early-payout-row-action-button"]}
            icon={<EyeOutlined />}
            aria-label={`View rule for ${selector.details}`}
            disabled={!selector.rule}
            onClick={() => handleViewSelectorRule(selector)}
          />
        ),
      },
      {
        title: EARLY_PAYOUT_TABLE_LABELS.actions,
        key: "actions",
        width: 130,
        render: (_, selector) => (
          <div className={styles["early-payout-selector-actions"]}>
            <Button
              type="text"
              className={`${styles["early-payout-row-action-button"]} ${styles["early-payout-delete-action-button"]}`}
              icon={<DeleteOutlined />}
              aria-label={`Delete selector ${selector.selectorId}`}
              onClick={() => handleDeleteSelector(selector)}
            />
            <Switch
              checked={selector.status === "Enabled"}
              loading={
                isUpdatingSelector && togglingSelectorId === selector.selectorId
              }
              className={`${styles["early-payout-selector-switch"]} ${
                selector.status === "Enabled"
                  ? styles["early-payout-selector-switch-active"]
                  : styles["early-payout-selector-switch-inactive"]
              }`}
              aria-label={`Toggle selector ${selector.selectorId}`}
              onChange={(checked) =>
                handleToggleSelectorStatus(selector, checked)
              }
            />
          </div>
        ),
      },
    ],
    [
      handleDeleteSelector,
      handleToggleSelectorStatus,
      handleViewSelectorRule,
      isUpdatingSelector,
      togglingSelectorId,
    ],
  );

  return (
    <>
      <div className={styles["early-payout-page"]} data-testid="early-payout">
        <div className={styles["early-payout-header"]}>
          <div className={styles["early-payout-header-actions"]}>
            <Button
              type="default"
              className={styles["early-payout-icon-button"]}
              icon={<DownloadOutlined />}
              aria-label={EARLY_PAYOUT_ACTION_LABELS.download}
              onClick={handleExport}
            />
          </div>
        </div>

        <div className={styles["early-payout-toolbar"]}>
          <Segmented<EarlyPayoutActiveTab>
            className={styles["early-payout-tabs"]}
            value={activeTab}
            onChange={handleTabChange}
            options={tabs}
          />
          {!isReportingTabActive && (
            <Button
              type="primary"
              className={styles["early-payout-create-button"]}
              icon={<PlusOutlined />}
              onClick={handleCreateAction}
            >
              {isRulesetsTabActive
                ? EARLY_PAYOUT_ACTION_LABELS.createRuleset
                : EARLY_PAYOUT_ACTION_LABELS.createSelector}
            </Button>
          )}
        </div>

        <div
          className={styles["early-payout-table-shell"]}
          data-testid={
            isReportingTabActive
              ? EARLY_PAYOUT_TEST_IDS.reportingTable
              : isRulesetsTabActive
                ? EARLY_PAYOUT_TEST_IDS.rulesetsTable
                : EARLY_PAYOUT_TEST_IDS.selectorsTable
          }
        >
          {isRulesetsTabActive && (
            <Table<EarlyPayoutRuleset>
              className={styles["early-payout-table"]}
              columns={rulesetColumns}
              dataSource={rulesets}
              loading={isRulesetsLoading}
              pagination={false}
              rowKey="id"
              tableLayout="fixed"
              showSorterTooltip={false}
            />
          )}
          {isSelectorsTabActive && (
            <Table<EarlyPayoutSelector>
              className={styles["early-payout-table"]}
              columns={selectorColumns}
              dataSource={selectors}
              loading={isSelectorsLoading}
              pagination={false}
              rowKey="selectorId"
              tableLayout="fixed"
              showSorterTooltip={false}
            />
          )}
          {isReportingTabActive && (
            <Table<EarlyPayoutReportingRow>
              className={styles["early-payout-table"]}
              columns={EARLY_PAYOUT_REPORTING_COLUMNS}
              // TODO(SPENG-2296): swap MOCK_REPORTING_ROWS for the reporting API data.
              dataSource={MOCK_REPORTING_ROWS}
              pagination={false}
              rowKey="id"
              tableLayout="fixed"
              showSorterTooltip={false}
            />
          )}
        </div>

        <Modal
          className={styles["early-payout-modal"]}
          title={
            isEditingRuleset
              ? EARLY_PAYOUT_MODAL_TITLES.editRuleset
              : EARLY_PAYOUT_MODAL_TITLES.createRuleset
          }
          open={isCreateRulesetOpen}
          onCancel={handleCloseCreateRuleset}
          footer={null}
          centered
          destroyOnHidden
        >
          <Form
            form={rulesetForm}
            className={styles["early-payout-modal-form"]}
            layout="vertical"
            initialValues={{ ruleType: "WINNER" }}
            onFinish={handleCreateRulesetSubmit}
          >
            {!isEditingRuleset && isDevMode && (
              <Form.Item
                label={EARLY_PAYOUT_FORM_LABELS.ruleType}
                name="ruleType"
                rules={[{ required: true, message: "Rule Type is required" }]}
              >
                <Select
                  placeholder={EARLY_PAYOUT_PLACEHOLDERS.ruleType}
                  options={EARLY_PAYOUT_RULE_TYPE_OPTIONS}
                />
              </Form.Item>
            )}
            <Form.Item
              label={EARLY_PAYOUT_FORM_LABELS.ruleName}
              name="ruleName"
              rules={[{ required: true, message: "Rule Name is required" }]}
            >
              <Input placeholder={EARLY_PAYOUT_PLACEHOLDERS.ruleName} />
            </Form.Item>
            {isEditingRuleset ? (
              <>
                <Form.Item label={EARLY_PAYOUT_FORM_LABELS.ruleType}>
                  <Input
                    disabled
                    value={normalizeEarlyPayoutRuleType(selectedRuleset?.ruleType)}
                  />
                </Form.Item>
                <Form.Item label={EARLY_PAYOUT_FORM_LABELS.sport}>
                  <Input disabled value={selectedRuleset?.sport ?? ""} />
                </Form.Item>
                <Form.Item label={EARLY_PAYOUT_FORM_LABELS.market}>
                  <Input
                    disabled
                    value={
                      selectedRuleset
                        ? getEarlyPayoutRulesetMarketLabel(selectedRuleset)
                        : ""
                    }
                  />
                </Form.Item>
                {isEditTotalRuleType ? (
                  <SoccerTotalConditions form={rulesetForm} />
                ) : (
                  <Form.Item
                    label={EARLY_PAYOUT_FORM_LABELS.advantage}
                    name="advantage"
                    rules={EARLY_PAYOUT_ADVANTAGE_RULES}
                  >
                    <InputNumber
                      placeholder={EARLY_PAYOUT_PLACEHOLDERS.advantage}
                      {...EARLY_PAYOUT_ADVANTAGE_INPUT_PROPS}
                    />
                  </Form.Item>
                )}
              </>
            ) : (
              <>
                <Form.Item
                  label={EARLY_PAYOUT_FORM_LABELS.sport}
                  name="sportId"
                  rules={[{ required: true, message: "Sport is required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={isSportsDropdownLoading}
                    placeholder={EARLY_PAYOUT_PLACEHOLDERS.sport}
                    options={sportSelectOptions}
                  />
                </Form.Item>
                <Form.Item
                  label={EARLY_PAYOUT_FORM_LABELS.market}
                  name="marketId"
                  rules={[{ required: true, message: "Market is required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={isMarketsDropdownLoading}
                    placeholder={EARLY_PAYOUT_PLACEHOLDERS.market}
                    options={marketSelectOptions}
                  />
                </Form.Item>
                {isCreateTotalRuleType ? (
                  <SoccerTotalConditions form={rulesetForm} />
                ) : (
                  <Form.Item
                    label={EARLY_PAYOUT_FORM_LABELS.advantage}
                    name="advantage"
                    rules={EARLY_PAYOUT_ADVANTAGE_RULES}
                  >
                    <InputNumber
                      placeholder={EARLY_PAYOUT_PLACEHOLDERS.advantage}
                      {...EARLY_PAYOUT_ADVANTAGE_INPUT_PROPS}
                    />
                  </Form.Item>
                )}
              </>
            )}
            <Form.Item className={styles["early-payout-modal-footer"]}>
              <Button
                type="primary"
                htmlType="submit"
                className={styles["early-payout-modal-submit"]}
                loading={
                  (isEditingRuleset && isUpdatingRuleset) ||
                  (!isEditingRuleset && isCreatingRuleset)
                }
              >
                {isEditingRuleset
                  ? EARLY_PAYOUT_ACTION_LABELS.updateRuleset
                  : EARLY_PAYOUT_ACTION_LABELS.createRuleset}
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          className={styles["early-payout-modal"]}
          title={EARLY_PAYOUT_MODAL_TITLES.createSelector}
          open={isCreateSelectorOpen}
          onCancel={handleCloseCreateSelector}
          footer={null}
          centered
          destroyOnHidden
        >
          <Form
            form={selectorForm}
            className={styles["early-payout-modal-form"]}
            layout="vertical"
            initialValues={{ selector: "sport" }}
            onFinish={handleCreateSelectorSubmit}
          >
            <Form.Item
              label={EARLY_PAYOUT_FORM_LABELS.selectorType}
              name="selector"
              rules={[{ required: true, message: "Selector type is required" }]}
            >
              <Select
                placeholder={EARLY_PAYOUT_PLACEHOLDERS.selector}
                options={SELECTOR_OPTIONS}
                onChange={handleSelectorTypeChange}
              />
            </Form.Item>
            {canCreateSelectorType ? (
              <>
                <Form.Item
                  label={EARLY_PAYOUT_FORM_LABELS.sport}
                  name="sportId"
                  rules={[{ required: true, message: "Sport is required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={isSportsDropdownLoading}
                    placeholder={EARLY_PAYOUT_PLACEHOLDERS.sport}
                    options={sportSelectOptions}
                    onChange={handleSelectorSportChange}
                  />
                </Form.Item>
                {needsLeagueDropdown ? (
                  <Form.Item
                    label={EARLY_PAYOUT_FORM_LABELS.tournament}
                    name="tournamentId"
                    rules={[
                      { required: true, message: "Tournament is required" },
                    ]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      disabled={!selectedSportId}
                      loading={isLeaguesDropdownLoading}
                      placeholder={EARLY_PAYOUT_PLACEHOLDERS.tournament}
                      options={leagueSelectOptions}
                      onChange={handleSelectorTournamentChange}
                      notFoundContent={
                        selectedSportId
                          ? "No tournaments available for this sport"
                          : "Select a sport first"
                      }
                    />
                  </Form.Item>
                ) : null}
                {isEventSelectorType ? (
                  <Form.Item
                    label={EARLY_PAYOUT_FORM_LABELS.event}
                    name="eventId"
                    rules={[{ required: true, message: "Event is required" }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      disabled={!selectedTournamentId}
                      loading={isEventsDropdownLoading}
                      placeholder={EARLY_PAYOUT_PLACEHOLDERS.event}
                      options={eventSelectOptions}
                      notFoundContent={
                        selectedTournamentId
                          ? "No events available for this tournament"
                          : "Select a tournament first"
                      }
                    />
                  </Form.Item>
                ) : null}
                <Form.Item
                  label={EARLY_PAYOUT_FORM_LABELS.rule}
                  name="ruleId"
                  rules={[{ required: true, message: "Rule is required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    disabled={!selectedSportId}
                    placeholder={EARLY_PAYOUT_PLACEHOLDERS.rule}
                    options={selectorSportRuleOptions}
                    notFoundContent={
                      selectedSportId
                        ? "No rules available for this sport"
                        : "Select a sport first"
                    }
                  />
                </Form.Item>
              </>
            ) : null}
            <Form.Item className={styles["early-payout-modal-footer"]}>
              <Button
                type="primary"
                htmlType="submit"
                className={styles["early-payout-modal-submit"]}
                disabled={!canCreateSelectorType}
                loading={isCreatingSelector}
              >
                {EARLY_PAYOUT_ACTION_LABELS.createSelector}
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          className={styles["early-payout-modal"]}
          title={EARLY_PAYOUT_MODAL_TITLES.viewSelectorRule}
          open={Boolean(selectorRuleViewTarget)}
          onCancel={handleCloseSelectorRuleModal}
          footer={null}
          centered
          destroyOnHidden
          data-testid={EARLY_PAYOUT_TEST_IDS.selectorRuleModal}
        >
          {selectorRuleViewTarget?.rule ? (
            <Form
              className={styles["early-payout-modal-form"]}
              layout="vertical"
            >
              <Form.Item label={EARLY_PAYOUT_FORM_LABELS.ruleName}>
                <Input
                  disabled
                  value={selectorRuleViewTarget.rule.ruleName}
                />
              </Form.Item>
              <Form.Item label={EARLY_PAYOUT_FORM_LABELS.ruleType}>
                <Input disabled value={selectorRuleViewRuleType} />
              </Form.Item>
              <Form.Item label={EARLY_PAYOUT_FORM_LABELS.sport}>
                <Input disabled value={selectorRuleViewTarget.rule.sport} />
              </Form.Item>
              <Form.Item label={EARLY_PAYOUT_FORM_LABELS.market}>
                <Input
                  disabled
                  value={getEarlyPayoutRulesetMarketLabel(
                    selectorRuleViewTarget.rule,
                  )}
                />
              </Form.Item>
              {isSelectorRuleViewTotal ? (
                <SoccerTotalConditionsView
                  conditions={selectorRuleViewTarget.rule.lines ?? []}
                />
              ) : (
                <Form.Item label={EARLY_PAYOUT_FORM_LABELS.advantage}>
                  <InputNumber
                    disabled
                    value={selectorRuleViewTarget.rule.advantage}
                    {...EARLY_PAYOUT_ADVANTAGE_INPUT_PROPS}
                  />
                </Form.Item>
              )}
              <Form.Item className={styles["early-payout-modal-footer"]}>
                <Button
                  type="primary"
                  className={styles["early-payout-modal-submit"]}
                  onClick={handleCloseSelectorRuleModal}
                >
                  {EARLY_PAYOUT_ACTION_LABELS.close}
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <p className={styles["early-payout-rule-empty"]}>
              No rule data available for this selector.
            </p>
          )}
        </Modal>

        <Modal
          className={styles["early-payout-modal"]}
          title={deleteModalTitle}
          open={Boolean(pendingDeleteTarget)}
          onCancel={handleCloseDeleteModal}
          footer={null}
          centered
          destroyOnHidden
        >
          <div className={styles["early-payout-delete-modal-content"]}>
            {pendingDeleteTarget?.type === "ruleset" ? (
              <>
                <p className={styles["early-payout-delete-modal-label"]}>
                  {EARLY_PAYOUT_TABLE_LABELS.ruleName}
                </p>
                <p className={styles["early-payout-delete-modal-target"]}>
                  {pendingDeleteTarget.record.ruleName}
                </p>
                <p className={styles["early-payout-delete-modal-label"]}>
                  {EARLY_PAYOUT_TABLE_LABELS.ruleType}
                </p>
                <p className={styles["early-payout-delete-modal-target"]}>
                  {normalizeEarlyPayoutRuleType(pendingDeleteTarget.record.ruleType)}
                </p>
                <p className={styles["early-payout-delete-modal-message"]}>
                  {EARLY_PAYOUT_CONFIRM_MESSAGES.deleteRulesetContent}
                </p>
              </>
            ) : pendingDeleteTarget?.type === "selector" ? (
              <>
                <p className={styles["early-payout-delete-modal-label"]}>
                  {EARLY_PAYOUT_TABLE_LABELS.selector}
                </p>
                <p className={styles["early-payout-delete-modal-target"]}>
                  {pendingDeleteTarget.record.details}
                </p>
                <p className={styles["early-payout-delete-modal-message"]}>
                  {EARLY_PAYOUT_CONFIRM_MESSAGES.deleteSelectorContent}
                </p>
              </>
            ) : null}
          </div>
          <div className={styles["early-payout-modal-footer"]}>
            <Button onClick={handleCloseDeleteModal}>
              {EARLY_PAYOUT_CONFIRM_MESSAGES.cancelText}
            </Button>
            <Button
              type="primary"
              className={styles["early-payout-modal-delete-button"]}
              loading={
                (pendingDeleteTarget?.type === "ruleset" && isDeletingRuleset) ||
                (pendingDeleteTarget?.type === "selector" && isDeletingSelector)
              }
              onClick={handleConfirmDelete}
            >
              {EARLY_PAYOUT_CONFIRM_MESSAGES.okText}
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default EarlyPayout;
